/**
 * The GMS order of events inside one discipline: every Men's event, then
 * every Women's, Mixed, Open — and inside a gender the same kind of event
 * together, shortest first: "Men's 50m / 100m / 200m Backstroke", then
 * "Men's 50m / 100m / 200m Breaststroke", … then "Women's 50m Backstroke"
 * (user, 2026-10-30; it replaced "genders side by side").
 *
 * The key, after the discipline:
 *   1. the gender, in `EVENT_GENDERS` order (MEN, WOMEN, MIXED, OPEN).
 *   2. the event's BASE — its name with the leading gender word removed
 *      (every event name reads "Men's …" / "Women's …" / "Mixed …" /
 *      "Open …"), lower-cased; a relay's leading multiplier moved behind the
 *      leg ("4x100m medley relay" → "100m medley relay x4"); a weight's sign
 *      settled ("-60kg" → "60kg", "+87kg" → "87kg+", so +87 follows -87);
 *      then the leading measure moved to the end ("50m backstroke" →
 *      "backstroke 50m", "medley relay x4 100m"), so events sort by their
 *      words first and their distance second; digit runs zero-padded to
 *      `EVENT_ORDER_DIGIT_PAD` so 200m sorts before 1500m. Compared byte by
 *      byte (`COLLATE "C"` in SQL, `<` here) — digits before letters, so a
 *      bare "100m" (athletics) sorts ahead of "hurdles 110m".
 *   3. the code (byte order), then the id.
 *
 * ONE rule, two spellings: `compareEvents` here (client-side lists) and
 * `eventOrderSql` in the API (`db/queries/event-order.ts`), which reads the
 * patterns and the pad from this file. An integration test runs both over
 * the same names and pins them equal.
 */
import { EVENT_GENDERS, type EventGender } from "./vocab";

/**
 * The leading gender word of an event name, with what follows it. Written
 * so JavaScript and Postgres' ARE read it the same (no `\s`/`\d` classes,
 * case-insensitive flag applied by each side).
 */
export const EVENT_GENDER_PREFIX_PATTERN = "^((men|women)(['’]s)?|mixed|open)[ ]+";

/** A relay's "4x" multiplier at the start of the base; moved to its end (`$2 x$1`). */
export const EVENT_RELAY_PATTERN = "^([0-9]+) *x *([0-9].*)$";

/** A weight's minus sign at the start of the base, dropped (`$1`). */
export const EVENT_MINUS_PATTERN = "^-([0-9])";

/** A weight's plus sign at the start of the base, moved behind its measure (`$1+`). */
export const EVENT_PLUS_PATTERN = "^[+]([0-9][0-9.a-z]*)";

/** A leading measure ("50m", "60kg", "87kg+") with words after it; moved to the end (`$2 $1`). */
export const EVENT_MEASURE_PATTERN = "^([0-9][0-9.a-z]*[+]?) +(.+)$";

/** Digit runs are left-padded with zeros to this width before comparing. */
export const EVENT_ORDER_DIGIT_PAD = 8;

const PREFIX = new RegExp(EVENT_GENDER_PREFIX_PATTERN, "i");
const RELAY = new RegExp(EVENT_RELAY_PATTERN);
const MINUS = new RegExp(EVENT_MINUS_PATTERN);
const PLUS = new RegExp(EVENT_PLUS_PATTERN);
const MEASURE = new RegExp(EVENT_MEASURE_PATTERN);

/** The base an event's name sorts by — see the file comment. */
export function eventBaseKey(name: string): string {
	return name
		.replace(PREFIX, "")
		.replace(/^ +| +$/g, "")
		.toLowerCase()
		.replace(RELAY, "$2 x$1")
		.replace(MINUS, "$1")
		.replace(PLUS, "$1+")
		.replace(MEASURE, "$2 $1")
		.replace(/[0-9]+/g, (d) => d.replace(/^0+(?=[0-9])/, "").padStart(EVENT_ORDER_DIGIT_PAD, "0"));
}

const GENDER_RANK: Readonly<Record<EventGender, number>> = Object.fromEntries(EVENT_GENDERS.map((g, i) => [g, i])) as Record<EventGender, number>;

/** Rank of a gender in `EVENT_GENDERS`; an unknown one sorts last. */
export const eventGenderRank = (g: string): number => GENDER_RANK[g as EventGender] ?? EVENT_GENDERS.length;

const byteCompare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export interface EventOrderFields {
	name: string;
	gender: string;
	code: string;
	event_id?: string;
}

/**
 * Comparator for events of ONE discipline (callers that mix disciplines
 * compare the discipline first). Same verdict as the API's `eventOrderSql`.
 */
export function compareEvents(a: EventOrderFields, b: EventOrderFields): number {
	return (
		eventGenderRank(a.gender) - eventGenderRank(b.gender) ||
		byteCompare(eventBaseKey(a.name), eventBaseKey(b.name)) ||
		byteCompare(a.code, b.code) ||
		byteCompare(a.event_id ?? "", b.event_id ?? "")
	);
}

/** A sorted copy — the input is left alone. */
export const sortEvents = <T extends EventOrderFields>(events: readonly T[]): T[] => [...events].sort(compareEvents);
